<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { useRoute } from 'vue-router';
  import calendlyService, {
    CalendlyVerificationRequest,
    CalendlyVerificationResponse,
  } from '@/services/calendly/calendly.service';

  const route = useRoute();

  const isLoading = ref(true);
  const isVerified = ref(false);
  const calendlyUrl = ref('');
  const errorMessage = ref('');

  // Load Calendly script and return a promise when it's ready
  const loadCalendlyScript = (): Promise<void> => {
    return new Promise((resolve) => {
      // Check if script is already loaded
      if (
        document.querySelector(
          'script[src*="calendly.com/assets/external/widget.js"]',
        )
      ) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => {
        // Wait a bit for Calendly to be fully initialized
        setTimeout(() => resolve(), 500);
      };
      document.head.appendChild(script);
    });
  };

  // Initialize Calendly widget manually
  const initializeWidget = () => {
    const widgetElement = document.querySelector(
      '.calendly-inline-widget',
    ) as HTMLElement;
    if (widgetElement && calendlyUrl.value) {
      // Clear any existing content
      widgetElement.innerHTML = '';

      // Check if Calendly is available and initialize
      if ((window as any).Calendly) {
        (window as any).Calendly.initInlineWidget({
          url: calendlyUrl.value,
          parentElement: widgetElement,
        });
      }
    }
  };

  const verifyCode = async () => {
    try {
      const code = route.query.code as string;
      const userId = route.query.userId as string;

      if (!code || !userId) {
        errorMessage.value = 'Missing required parameters: code and userId';
        isLoading.value = false;
        return;
      }

      const verificationData: CalendlyVerificationRequest = {
        code,
        userId,
      };

      const response: CalendlyVerificationResponse =
        await calendlyService.verifyCode(verificationData);

      if (
        !response?.is_used &&
        response?.free_session?.free_session_scheduling_link
      ) {
        isVerified.value = true;

        // Get the base Calendly URL
        const baseCalendlyUrl =
          response?.free_session?.free_session_scheduling_link;

        // Create URL object to handle parameters properly
        const calendlyUrlObj = new URL(baseCalendlyUrl);

        // Forward all current URL parameters to Calendly URL (except code and userId)
        Object.entries(route.query).forEach(([key, value]) => {
          if (
            value !== null &&
            value !== undefined &&
            key !== 'code' &&
            key !== 'userId'
          ) {
            // Convert value to string and set parameter
            calendlyUrlObj.searchParams.set(key, String(value));
          }
        });

        calendlyUrl.value = calendlyUrlObj.toString();

        // Wait for script to load and then initialize widget
        await loadCalendlyScript();
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
          initializeWidget();
        }, 100);
      } else {
        errorMessage.value = 'Verification code is already used';
      }
    } catch (error) {
      console.error('Verification error:', error);
      errorMessage.value =
        'An error occurred during verification. Please try again.';
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(() => {
    verifyCode();
  });
</script>

<template>
  <div class="calendly-verification-wrapper">
    <div class="container">
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-container">
        <div class="spinner"></div>
        <p>Verifying your access...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="!isVerified" class="error-container">
        <div class="error-icon">⚠️</div>
        <h2>Verification Failed</h2>
        <p>{{ errorMessage }}</p>
        <button @click="verifyCode" class="retry-btn">Try Again</button>
      </div>

      <!-- Success state with Calendly widget -->
      <div v-else class="calendly-container">
        <h2>Schedule Your Session</h2>
        <div class="calendly-widget">
          <!-- Calendly inline widget begin -->
          <div
            class="calendly-inline-widget"
            style="min-width: 320px; height: 700px"
          ></div>
          <!-- Calendly inline widget end -->
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .calendly-verification-wrapper {
    min-height: 100vh;
    background: var(--gradient);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  .container {
    max-width: 800px;
    width: 100%;
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    overflow: hidden;
  }

  .loading-container,
  .error-container {
    text-align: center;
    padding: 60px 40px;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .loading-container p {
    color: #666;
    font-size: 16px;
    margin: 0;
  }

  .error-container {
    color: #d32f2f;
  }

  .error-icon {
    font-size: 48px;
    margin-bottom: 20px;
  }

  .error-container h2 {
    color: #d32f2f;
    margin-bottom: 16px;
    font-size: 28px;
  }

  .error-container p {
    color: #666;
    font-size: 16px;
    margin-bottom: 24px;
    line-height: 1.5;
  }

  .retry-btn {
    background: var(--color-black);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 6px;
    font-size: 16px;
    cursor: pointer;
    transition: background 0.3s;
  }

  .retry-btn:hover {
    background: var(--color-dark);
  }

  .calendly-container {
    padding: 40px;
  }

  .calendly-container h2 {
    text-align: center;
    color: #333;
    margin-bottom: 30px;
    font-size: 28px;
    font-weight: 600;
  }

  .calendly-widget {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .calendly-inline-widget {
    width: 100% !important;
    min-width: 320px;
    height: 700px;
  }

  @media (max-width: 768px) {
    .calendly-verification-wrapper {
      padding: 10px;
    }

    .container {
      margin: 0;
    }

    .loading-container,
    .error-container,
    .calendly-container {
      padding: 30px 20px;
    }

    .calendly-container h2 {
      font-size: 24px;
    }

    .calendly-widget iframe {
      min-height: 500px;
    }
  }
</style>
